import React, { useEffect, useState } from 'react';
import { Shield, Save, RotateCcw, Key, Users, Check, AlertTriangle, Info } from 'lucide-react';
import { useApi } from './DashboardLayout';

const ROLE_NAMES = { '1': 'Super-Admin', '2': 'Admin', '3': 'Herzgruppenanbieter' };
const ROLE_DESCRIPTIONS = {
  '1': 'Voller Zugriff auf alle Bereiche. Nicht einschränkbar.',
  '2': 'Verwaltung von Angeboten und Anbietern.',
  '3': 'Sieht und bearbeitet nur eigenes Profil und eigene Angebote.',
};

const AREA_LABELS = {
  dashboard: { label: 'Dashboard', desc: 'Statistiken und Übersicht' },
  angebote: { label: 'Angebote', desc: 'Angebote anzeigen und verwalten' },
  anbieter: { label: 'Anbieter', desc: 'Anbieterliste und -details' },
  zertifizierung: { label: 'Zertifizierung', desc: 'Angebote zertifizieren/entziehen' },
  archivierung: { label: 'Archivierung', desc: 'Benutzer und Angebote archivieren/wiederherstellen' },
  admin_verwaltung: { label: 'Admin-Verwaltung', desc: 'Administratoren anlegen und verwalten' },
  einstellungen: { label: 'Einstellungen', desc: 'Design, Dropdowns, SMTP, Rollen' },
};

const AREAS = ['dashboard', 'angebote', 'anbieter', 'zertifizierung', 'archivierung', 'admin_verwaltung', 'einstellungen'];
const LEVELS = ['keine', 'ansicht', 'bearbeiten'];
const LEVEL_LABELS = { keine: 'Keine', ansicht: 'Ansicht', bearbeiten: 'Bearbeiten' };

export default function RolesPage() {
  const [permissions, setPermissions] = useState(null);
  const [selectedRole, setSelectedRole] = useState('2');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [initPw, setInitPw] = useState('');
  const [settingPw, setSettingPw] = useState(false);
  const { apiFetch } = useApi();

  useEffect(() => { loadPermissions(); }, []);

  const loadPermissions = async () => {
    const r = await apiFetch('/api/settings/roles');
    const d = await r?.json();
    if (d?.permissions) setPermissions(d.permissions);
  };

  const showFb = (type, text) => { setFeedback({ type, text }); setTimeout(() => setFeedback(null), 5000); };

  const setPermission = (role, area, level) => {
    if (role === '1') return; // Super-Admin is locked
    setPermissions(prev => ({
      ...prev,
      [role]: { ...prev[role], [area]: level }
    }));
  };

  const savePermissions = async () => {
    setSaving(true);
    const r = await apiFetch('/api/settings/roles', { method: 'PUT', body: JSON.stringify({ permissions }) });
    const d = await r?.json();
    if (d?.status) showFb('success', 'Rollenberechtigungen gespeichert.');
    else showFb('error', d?.detail || 'Fehler.');
    setSaving(false);
  };

  const resetPermissions = async () => {
    setSaving(true);
    const r = await apiFetch('/api/settings/roles/reset', { method: 'POST' });
    const d = await r?.json();
    if (d?.status) { setPermissions(d.permissions); showFb('success', 'Auf Standard zurückgesetzt.'); }
    setSaving(false);
  };

  const setInitialPassword = async () => {
    if (!initPw || initPw.length < 8) { showFb('error', 'Passwort muss mindestens 8 Zeichen lang sein.'); return; }
    setSettingPw(true);
    const r = await apiFetch('/api/settings/initial-password', { method: 'POST', body: JSON.stringify({ password: initPw }) });
    const d = await r?.json();
    if (d?.status) { showFb('success', d.message); setInitPw(''); }
    else showFb('error', d?.detail || 'Fehler.');
    setSettingPw(false);
  };

  if (!permissions) return <div style={{ padding: '2rem', color: 'var(--text-secondary)' }}>Laden...</div>;

  return (
    <div data-testid="roles-page">
      <div className="page-header">
        <h2>Rollen & Berechtigungen</h2>
        <p>Bereichsberechtigungen pro Rolle verwalten</p>
      </div>

      {feedback && (
        <div className={`design-feedback ${feedback.type}`} data-testid="roles-feedback">
          {feedback.type === 'success' ? <Check size={14} /> : <AlertTriangle size={14} />}
          {feedback.text}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: '1.5rem', alignItems: 'start' }}>
        {/* Left: Role selector */}
        <div className="card">
          <div className="card-body" style={{ padding: 0 }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: 14 }}>
              <Shield size={15} style={{ marginRight: 6, verticalAlign: -2 }} /> Rollen
            </div>
            {['1', '2', '3'].map(role => (
              <div
                key={role}
                onClick={() => setSelectedRole(role)}
                data-testid={`role-select-${role}`}
                style={{
                  padding: '12px 16px', cursor: 'pointer',
                  borderBottom: '1px solid var(--border)',
                  background: selectedRole === role ? 'rgba(204,0,0,0.05)' : 'transparent',
                  borderLeft: selectedRole === role ? '3px solid var(--primary)' : '3px solid transparent',
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 13 }}>{ROLE_NAMES[role]}</div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{ROLE_DESCRIPTIONS[role]}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Permissions table */}
        <div>
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <div className="card-body">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <h4 style={{ fontSize: 16, fontWeight: 700 }}>Bereichsberechtigungen: {ROLE_NAMES[selectedRole]}</h4>
                  {selectedRole === '1' && (
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>Super-Admin hat vollen Zugriff und kann nicht eingeschränkt werden.</p>
                  )}
                  {selectedRole === '3' && (
                    <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                      Hinweis: "Ansicht" bei Angeboten erlaubt weiterhin das Bearbeiten eigener Angebote.
                    </p>
                  )}
                </div>
              </div>

              {AREAS.map(area => {
                const current = permissions[selectedRole]?.[area] || 'keine';
                const locked = selectedRole === '1';
                return (
                  <div
                    key={area}
                    className="roles-permission-row"
                    data-testid={`perm-row-${area}`}
                  >
                    <div className="roles-permission-label">
                      <span className="roles-area-name">{AREA_LABELS[area].label}</span>
                      <span className="roles-area-desc">{AREA_LABELS[area].desc}</span>
                    </div>
                    <div className="roles-level-group">
                      {LEVELS.map(level => (
                        <label
                          key={level}
                          className={`roles-level-btn ${current === level ? 'active' : ''} ${locked ? 'locked' : ''}`}
                          data-testid={`perm-${area}-${level}`}
                        >
                          <input
                            type="radio"
                            name={`${selectedRole}-${area}`}
                            checked={current === level}
                            onChange={() => setPermission(selectedRole, area, level)}
                            disabled={locked}
                            style={{ display: 'none' }}
                          />
                          {LEVEL_LABELS[level]}
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}

              <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                <button className="btn btn-primary" onClick={savePermissions} disabled={saving || selectedRole === '1'} data-testid="save-roles-btn">
                  <Save size={14} /> Speichern
                </button>
                <button className="btn btn-secondary" onClick={resetPermissions} disabled={saving} data-testid="reset-roles-btn">
                  <RotateCcw size={14} /> Auf Standard zurücksetzen
                </button>
              </div>
            </div>
          </div>

          {/* Initial Password Section */}
          <div className="card">
            <div className="card-body">
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <Key size={18} style={{ color: 'var(--text-secondary)' }} />
                <h4 style={{ fontSize: 16, fontWeight: 700 }}>Initial-Passwort für Herzgruppenanbieter</h4>
              </div>
              <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Setzt ein einheitliches Passwort für <strong>alle</strong> Herzgruppenanbieter (Typ 3). Beim ersten Login werden sie aufgefordert, ihr Passwort zu ändern.
              </p>
              <div className="design-info-box" style={{ marginBottom: 16, background: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.2)', color: '#92400E' }}>
                <AlertTriangle size={14} />
                <div>Achtung: Dies überschreibt das aktuelle Passwort <strong>aller</strong> Herzgruppenanbieter. Die Club-ID bleibt als Benutzername erhalten.</div>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                <div style={{ flex: 1, maxWidth: 320 }}>
                  <label className="smtp-label">Neues Initial-Passwort</label>
                  <input
                    className="form-input"
                    type="text"
                    placeholder="Min. 8 Zeichen"
                    value={initPw}
                    onChange={e => setInitPw(e.target.value)}
                    data-testid="initial-password-input"
                  />
                </div>
                <button className="btn btn-primary" onClick={setInitialPassword} disabled={settingPw} data-testid="set-initial-password-btn" style={{ height: 40 }}>
                  <Key size={14} /> {settingPw ? 'Setze...' : 'Passwort setzen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
